import React, { useState } from 'react';
import { Box, IconButton, Typography } from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const ToolOutputMessage = ({ message, nested = false }) => {
  const [expanded, setExpanded] = useState(false);
  const hasError = message.tool_output?.error;
  const hasOutput = message.tool_output && Object.keys(message.tool_output).length > 0;

  const content = (
    <>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.75,
          borderRadius: 'md',
          bgcolor: (theme) => theme.palette.mode === 'dark'
            ? (hasError ? 'danger.900' : 'success.900')
            : (hasError ? 'danger.50' : 'success.50'),
          border: '1px solid',
          borderColor: (theme) => theme.palette.mode === 'dark'
            ? (hasError ? 'danger.700' : 'success.700')
            : (hasError ? 'danger.200' : 'success.200'),
          cursor: hasOutput ? 'pointer' : 'default',
          transition: 'all 0.15s ease',
          '&:hover': hasOutput
            ? {
              bgcolor: (theme) => theme.palette.mode === 'dark'
                ? (hasError ? 'danger.800' : 'success.800')
                : (hasError ? 'danger.100' : 'success.100'),
              borderColor: (theme) => theme.palette.mode === 'dark'
                ? (hasError ? 'danger.600' : 'success.600')
                : (hasError ? 'danger.300' : 'success.300'),
            }
            : {},
        }}
        onClick={() => hasOutput && setExpanded(!expanded)}
      >
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: 'sm',
            bgcolor: (theme) => theme.palette.mode === 'dark'
              ? (hasError ? 'danger.800' : 'success.800')
              : (hasError ? 'danger.100' : 'success.100'),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {hasError ? (
            <ErrorOutlineIcon sx={{ fontSize: 14, color: (theme) => theme.palette.mode === 'dark' ? 'danger.200' : 'danger.600' }} />
          ) : (
            <CheckCircleOutlinedIcon sx={{ fontSize: 14, color: (theme) => theme.palette.mode === 'dark' ? 'success.200' : 'success.600' }} />
          )}
        </Box>

        <Typography
          level="body-sm"
          sx={{
            fontWeight: 500,
            color: (theme) => theme.palette.mode === 'dark'
              ? (hasError ? 'danger.200' : 'success.200')
              : (hasError ? 'danger.700' : 'success.700'),
          }}
        >
          {message.content || `${message.tool_name || 'Tool'} ${hasError ? 'failed' : 'completed'}`}
        </Typography>

        {hasOutput && (
          <IconButton
            size="sm"
            variant="plain"
            color={hasError ? 'danger' : 'success'}
            sx={{
              '--IconButton-size': '20px',
              ml: 0.5,
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          >
            <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>

      <AnimatePresence>
        {expanded && hasOutput && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Box
              component="pre"
              sx={{
                mt: 1,
                p: 1.5,
                bgcolor: (theme) => theme.palette.mode === 'dark'
                  ? (hasError ? 'danger.900' : 'success.900')
                  : (hasError ? 'danger.100' : 'success.100'),
                color: (theme) => theme.palette.mode === 'dark'
                  ? (hasError ? 'danger.100' : 'success.100')
                  : (hasError ? 'danger.900' : 'success.900'),
                borderRadius: 'md',
                fontSize: '0.75rem',
                fontFamily: 'monospace',
                overflow: 'auto',
                maxHeight: 200,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                border: '1px solid',
                borderColor: (theme) => theme.palette.mode === 'dark'
                  ? (hasError ? 'danger.700' : 'success.700')
                  : (hasError ? 'danger.200' : 'success.200'),
              }}
            >
              {JSON.stringify(message.tool_output, null, 2)}
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );

  if (nested) return content;

  return <Box sx={{ mb: 2, pl: 7 }}>{content}</Box>;
};

export default ToolOutputMessage;
